import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';

interface TowerGameProps {
  activeCurrency: string;
  onBetPlaced: (amount: number) => Promise<boolean>;
  onWinClaimed: (multiplier: number) => void;
  balance: number;
}

export default function TowerGame({ activeCurrency, onBetPlaced, onWinClaimed, balance }: TowerGameProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [betAmount, setBetAmount] = useState(1.0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentLevel, setCurrentLevel] = useState(0);
  const [multiplier, setMultiplier] = useState(1.0);

  const sceneRef = useRef<THREE.Scene | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const blocksRef = useRef<THREE.Mesh[]>([]);

  useEffect(() => {
    if (!containerRef.current) return;

    // 1. 初始化 2.5D 等距透视 Scene
    const scene = new THREE.Scene();
    sceneRef.current = scene;

    // 等距相机 (Orthographic Camera)
    const aspect = containerRef.current.clientWidth / containerRef.current.clientHeight;
    const d = 5;
    const camera = new THREE.OrthographicCamera(-d * aspect, d * aspect, d, -d, 1, 1000);
    
    // 定位等距夹角 (45度倾斜)
    camera.position.set(10, 10, 10);
    camera.lookAt(0, 0, 0);

    // 2. 渲染器
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    containerRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // 3. 光源与阴影
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.4);
    scene.add(ambientLight);

    const dirLight = new THREE.DirectionalLight(0x00ffff, 0.8);
    dirLight.position.set(5, 10, 7);
    scene.add(dirLight);

    // 4. 地面底座网格
    const baseGeo = new THREE.BoxGeometry(4, 0.5, 4);
    const baseMat = new THREE.MeshLambertMaterial({ color: 0x2c3e50 });
    const baseMesh = new THREE.Mesh(baseGeo, baseMat);
    baseMesh.position.y = -0.25;
    scene.add(baseMesh);

    // 动画循环
    let animId: number;
    const animate = () => {
      animId = requestAnimationFrame(animate);
      
      // 场景轻微自转
      scene.rotation.y += 0.003;
      
      renderer.render(scene, camera);
    };
    animate();

    // Resize 处理
    const handleResize = () => {
      if (!containerRef.current || !rendererRef.current) return;
      const width = containerRef.current.clientWidth;
      const height = containerRef.current.clientHeight;
      const aspect = width / height;
      camera.left = -d * aspect;
      camera.right = d * aspect;
      camera.top = d;
      camera.bottom = -d;
      camera.updateProjectionMatrix();
      rendererRef.current.setSize(width, height);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', handleResize);
      if (rendererRef.current && containerRef.current) {
        containerRef.current.removeChild(rendererRef.current.domElement);
        rendererRef.current.dispose();
      }
    };
  }, []);

  // 增加新一层塔块 (下注/前行时触发)
  const addTowerBlock = () => {
    const scene = sceneRef.current;
    if (!scene) return;

    const level = currentLevel + 1;
    const yPos = level * 0.6; // 每层高度偏移
    
    const geo = new THREE.BoxGeometry(3 - level * 0.15, 0.5, 3 - level * 0.15); // 塔身往上越尖
    // 彩色霓虹渐变层
    const color = new THREE.Color().setHSL((level * 30) / 360, 0.9, 0.6);
    const mat = new THREE.MeshLambertMaterial({ color, transparent: true, opacity: 0.9 });
    const block = new THREE.Mesh(geo, mat);
    
    // 入场动效初始位置 (从天而降)
    block.position.set(0, yPos + 2, 0);
    scene.add(block);
    blocksRef.current.push(block);

    // 平滑坠落动效
    const targetY = yPos - 0.25;
    const dropAnimation = () => {
      if (block.position.y > targetY) {
        block.position.y -= 0.15;
        requestAnimationFrame(dropAnimation);
      } else {
        block.position.y = targetY;
      }
    };
    dropAnimation();

    setCurrentLevel(level);
    setMultiplier(prev => Math.round((prev + 0.25 * level) * 100) / 100);
  };

  const handleStart = async () => {
    if (balance < betAmount) {
      alert('余额不足！');
      return;
    }
    
    const success = await onBetPlaced(betAmount);
    if (success) {
      setIsPlaying(true);
      setCurrentLevel(0);
      setMultiplier(1.0);
      
      // 清理原有层级网格
      const scene = sceneRef.current;
      if (scene) {
        blocksRef.current.forEach(b => scene.remove(b));
        blocksRef.current = [];
      }
      
      // 添加第一层
      addTowerBlock();
    }
  };

  const handleClimb = () => {
    if (!isPlaying) return;

    // 模拟前行攀爬概率 (80% 成功，20% 失败倒塌)
    const isSuccess = Math.random() > 0.2;
    
    if (isSuccess) {
      addTowerBlock();
    } else {
      setIsPlaying(false);
      alert('💥 塔倾倒了！游戏结束，投注额归零！');
      onWinClaimed(0);
      
      // 倒塌的物理重力掉落动画
      blocksRef.current.forEach((block, idx) => {
        const drop = () => {
          if (block.position.y > -2) {
            block.position.y -= 0.25;
            block.rotation.z += 0.05 * (idx + 1);
            block.rotation.x += 0.02;
            requestAnimationFrame(drop);
          }
        };
        drop();
      });
    }
  };

  const handleCashout = () => {
    if (!isPlaying) return;
    onWinClaimed(multiplier);
    setIsPlaying(false);
    alert(`🎉 止盈出局！获得 ${multiplier.toFixed(2)}x 收益！`);
  };

  return (
    <div className="glass-card" style={{ padding: '12px' }}>
      <h3 style={{ margin: '8px 12px', fontSize: '18px', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: 'var(--color-secondary)' }}></span>
        9F Tower 2.5D (Three.js 渲染爬塔)
      </h3>
      
      <div className="game-frame" ref={containerRef} style={{ height: '240px' }}>
        {/* Three.js Canvas 将挂载于此 */}
      </div>

      <div style={{ display: 'flex', gap: '12px', padding: '0 12px 12px' }}>
        {!isPlaying ? (
          <>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: '12px', color: 'var(--text-muted)' }}>投注额 ({activeCurrency.toUpperCase()})</label>
              <input
                type="number"
                value={betAmount}
                onChange={(e) => setBetAmount(Number(e.target.value))}
                style={{
                  width: '100%',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid var(--border-glass)',
                  color: 'var(--text-main)',
                  padding: '10px',
                  borderRadius: '8px',
                  marginTop: '4px',
                  fontFamily: 'var(--font-primary)'
                }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', flex: 1 }}>
              <button className="btn-neon" onClick={handleStart} style={{ width: '100%', padding: '12px', background: 'linear-gradient(90deg, var(--color-secondary), var(--color-accent))' }}>
                开始游戏
              </button>
            </div>
          </>
        ) : (
          <div style={{ display: 'flex', width: '100%', gap: '12px' }}>
            <button 
              className="btn-neon" 
              onClick={handleClimb} 
              style={{ flex: 1, padding: '12px', background: 'linear-gradient(90deg, var(--color-primary), var(--color-secondary))' }}
            >
              继续攀爬 ({multiplier.toFixed(2)}x)
            </button>
            <button 
              className="btn-neon" 
              onClick={handleCashout} 
              style={{ flex: 1, padding: '12px', background: 'linear-gradient(90deg, var(--color-accent), var(--color-secondary))' }}
            >
              止盈出局
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
